import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { academyApi } from "@/lib/academy";
import { BELT_COLORS, BELT_INK } from "@/lib/academy";
import AcademySuggest from "@/components/AcademySuggest";

export default function AcademyDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    academyApi.dashboard().then(setData).catch((e) => setError(e.response?.status === 401 ? "Please sign in." : "Could not load."));
  }, []);

  if (error) {
    return (
      <section className="wrap" style={{ padding: "80px 20px", textAlign: "center" }}>
        <p className="lead">{error}</p>
        <Link className="btn lime" to="/academy/register">Sign in or enroll ↗</Link>
      </section>
    );
  }
  if (!data) return <section className="wrap" style={{ padding: 80 }}><p>Loading your dojo…</p></section>;

  const next = (data.belts || []).flatMap((b) => b.lessons.map((l) => ({ ...l, belt: b })))
    .find((l) => l.sectionsDone < 3 && l.belt.unlocked);

  return (
    <>
      <Seo title="My dashboard | Solar Black Belt Academy" />
      <section className="page-hero"><div className="wrap">
        <span className="eyebrow">STUDENT DASHBOARD</span>
        <h1>Train on, {data.member.displayName}.</h1>
        {!data.member.emailVerified && <p className="micro" style={{ color: "#a33" }}>Verify your email (check your inbox) to unlock graded belt exams.</p>}
      </div></section>
      <section className="wrap academy-section">
        <div className="academy-cert-grid" data-testid="academy-dashboard-next">
          <div>
            <h3>Resume</h3>
            {next
              ? <p><Link to={`/academy/lessons/${next.id}`} data-testid="academy-resume-link">{next.title} — {next.belt.name} Belt, section {next.sectionsDone + 1}/3 →</Link></p>
              : <p>All published lessons complete. Try a belt exam or the capstone.</p>}
          </div>
          <div>
            <h3>Spaced review</h3>
            {data.dueReview?.length
              ? <p>{data.dueReview.length} items due — <Link to="/academy/dashboard">review your flashcards</Link> after your next lesson.</p>
              : <p>Nothing due. Answer practice questions to build your review queue.</p>}
          </div>
          <div>
            <h3>The official test</h3>
            {data.comprehensive?.passed
              ? <p>🏆 Comprehensive exam passed — Certified Solar &amp; Powerwall Professional is on your certificates list.</p>
              : data.comprehensive?.unlocked
                ? <p>Your Black Belt is earned — the <Link to="/academy/final" data-testid="academy-final-link">official comprehensive exam</Link> is open: every vocabulary word plus all-belt knowledge.</p>
                : <p>Earn your Black Belt to unlock the official comprehensive exam ({data.comprehensive?.vocabQuestions || 0}+ vocabulary questions are waiting inside the lessons).</p>}
          </div>
          <div>
            <h3>Certificates</h3>
            {data.certificates?.length
              ? data.certificates.map((c) => (
                <p key={c.id}><Link to={`/academy/certificates/${c.id}`}>{c.scope}</Link> — issued {c.issuedOn?.slice(0, 10)}</p>
              ))
              : <p>Earn your first by completing a belt.</p>}
          </div>
        </div>
      </section>
      <section className="wrap academy-section" data-testid="academy-dashboard-belts">
        <div className="academy-belt-grid">
          {data.belts.map((b) => (
            <Link to={`/academy/belts/${b.id}`} key={b.id} className="academy-belt-card" style={{ opacity: b.unlocked ? 1 : 0.45 }}>
              <span className="academy-belt-chip" style={{ background: BELT_COLORS[b.name], color: BELT_INK[b.name] }}>
                {b.name} Belt {data.passedBelts.includes(b.name) ? "✓ earned" : b.unlocked ? "" : "🔒 locked"}
              </span>
              <p>{b.lessons.map((l) => `${l.title} (${l.sectionsDone}/3)`).join(" · ")}</p>
              <span className="micro">Exam threshold {b.pass_percent}%</span>
            </Link>
          ))}
        </div>
        <p className="micro" style={{ marginTop: 18 }}>
          Labs and the Black Belt capstone live on the <Link to="/academy/capstone">labs page</Link>. When you finish,
          the optional <Link to="/academy/careers">hiring pipeline</Link> opens — never an automatic job offer.
        </p>
        <AcademySuggest page="/academy/dashboard" />
      </section>
    </>
  );
}
